import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../utils/api";

const LANGUAGE_OPTIONS = [
  "Spanish",
  "French",
  "German",
  "Japanese",
  "Chinese",
  "Korean",
  "Arabic",
  "Russian",
  "Hebrew",
  "English",
];

const TourDialog = ({ open, onClose }) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const currentLanguages = useMemo(() => {
    return Array.isArray(user?.profile?.languages) ? user.profile.languages : [];
  }, [user]);

  const activeLanguage = selectedLanguage || currentLanguages[0] || "your language";

  useEffect(() => {
    if (!open) return;
    setError("");
    setSelectedLanguage(currentLanguages[0] || "");
    setStep(currentLanguages.length ? 1 : 0);
  }, [open, currentLanguages]);

  const saveLanguage = async (language) => {
    setError("");
    setLoading(true);
    const nextLanguages = currentLanguages.includes(language)
      ? currentLanguages
      : [language, ...currentLanguages];

    try {
      await api.updateProfile(user.id, { languages: nextLanguages });
      updateUser((prevUser) => ({
        ...prevUser,
        profile: {
          ...prevUser.profile,
          languages: nextLanguages,
        },
      }));
      setSelectedLanguage(language);
      setStep(1);
    } catch (e) {
      console.error(e);
      setError("Could not save your language. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const goNext = () => {
    if (step === 0) {
      if (!selectedLanguage) {
        setError("Select a language to continue.");
        return;
      }
      saveLanguage(selectedLanguage);
      return;
    }
    setStep((prev) => Math.min(prev + 1, 2));
  };

  const stepButtons = () => {
    if (step === 0) {
      return (
        <Button variant="contained" onClick={goNext} disabled={loading || !selectedLanguage}>
          {loading ? "Saving..." : "Save language"}
        </Button>
      );
    }
    if (step < 2) {
      return <Button variant="contained" onClick={goNext}>Next</Button>;
    }
    return <Button variant="contained" onClick={onClose}>Finish</Button>;
  };

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="tour-dialog-title">
      <DialogTitle id="tour-dialog-title" sx={{ pb: 1 }}>
        {step === 0 && "Choose the language you want to learn"}
        {step === 1 && `Explore decks for ${activeLanguage}`}
        {step === 2 && "Discover roadmaps, resources, and community"}
      </DialogTitle>
      <DialogContent dividers>
        {step === 0 && (
          <Box>
            <Typography mb={2} color={palette.text.secondary}>
              Pick a primary language now and Uben will surface decks, guide paths, and resources for it.
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {LANGUAGE_OPTIONS.map((language) => (
                <Chip
                  key={language}
                  label={language}
                  clickable
                  color={selectedLanguage === language ? "primary" : "default"}
                  variant={selectedLanguage === language ? "filled" : "outlined"}
                  onClick={() => setSelectedLanguage(language)}
                  sx={{ minWidth: 100 }}
                />
              ))}
            </Box>
            <Typography variant="body2" color={palette.text.secondary}>
              Your choice will be saved to your profile and used to personalize the Explore page.
            </Typography>
            {loading && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {error && (
              <Typography mt={2} color="error">{error}</Typography>
            )}
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography mb={2} color={palette.text.secondary}>
              Great! Uben will now highlight decks and learning paths for {activeLanguage}.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography fontWeight={600} mb={1}>
              What to do next
            </Typography>
            <Typography mb={1}>• Browse recommended decks in Explore.</Typography>
            <Typography mb={1}>• Save decks you want to study first.</Typography>
            <Typography mb={1}>• Start a study session to build momentum.</Typography>
            <Box mt={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1}>
              <Button fullWidth variant="contained" onClick={() => handleNavigate("/explore")}>Go to Explore</Button>
              <Button fullWidth variant="outlined" onClick={goNext}>Show me resources</Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography mb={2} color={palette.text.secondary}>
              Uben has more than decks. Use Resources and Community to follow a complete learning path.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography fontWeight={600} mb={1}>
              Recommended actions
            </Typography>
            <Typography mb={1}>• Open Resources to browse learning content and study tools.</Typography>
            <Typography mb={1}>• Visit Community to connect with other learners.</Typography>
            <Typography mb={1}>• Use settings to update your languages anytime.</Typography>
            <Box mt={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1}>
              <Button fullWidth variant="contained" onClick={() => handleNavigate("/learn")}>Open Resources</Button>
              <Button fullWidth variant="outlined" onClick={() => handleNavigate("/community")}>Open Community</Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {stepButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default TourDialog;
